import * as Actions from '../actions'
import * as Constants from '../constants'
import * as Processes from '@rushplay/processes'
import * as R from 'ramda'
import * as Request from '../request'

import {bind} from 'redux-effects'
import {createPaymentMethodId} from '../create-provider-id'

function transformTransactionType(txType) {
  switch (txType) {
    case 'deposit': {
      return Constants.TransactionType.DEPOSIT
    }
    case 'withdraw': {
      return Constants.TransactionType.WITHDRAWAL
    }

    default: {
      throw new Error('Unknown transaction type')
    }
  }
}

function transformPaymentMethods(paymentMethods, txType) {
  return R.reduce(
    (methods, method) => {
      const transactionType = transformTransactionType(method.transaction_type)

      if (transactionType === txType) {
        const backendType = Constants.BackendType.TRANSFERWORLD

        return R.append(
          {
            accounts: [],
            backendType,
            canAddAccount: true,
            id: createPaymentMethodId({
              backendType,
              providerType: method.provider,
              transactionType,
            }),
            limit: {
              max: `${method.limits.max_cents / 100}`,
              min: `${method.limits.min_cents / 100}`,
            },
            providerType: method.provider,
            transactionType,
          },
          methods
        )
      }

      return methods
    },
    [],
    paymentMethods
  )
}

export function fetchPaymentMethods(params) {
  const processId = `transferworld/fetch-payments-methods`

  return [
    Processes.start(processId),
    bind(
      Request.createRequest({
        backendType: Constants.BackendType.TRANSFERWORLD,
        requestData: {
          countryCode: params.countryCode,
        },
        requestHeaders: {
          Accept: 'application/vnd.casinosaga.v1, application/json',
          Authorization: R.defaultTo('', params.sessionId),
        },
        requestMethod: 'GET',
        urlTemplate: '/transfer_world/payment_methods{?countryCode}',
      }),
      (res) => [
        Actions.updatePaymentMethods(
          transformPaymentMethods(res.value, params.transactionType)
        ),
        Processes.stop(processId),
      ],
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          // Pass real error to console for easier debugging
          // eslint-disable-next-line no-console
          console.error(error)
        }

        return [
          Actions.updatePaymentMethods(
            new Error('errors.transfer-world.fetch-payment-methods-failure')
          ),
          Processes.stop(processId),
        ]
      }
    ),
  ]
}

export function performTransaction(params, transactionParams) {
  const processId = `perform-transaction-${params.paymentMethodId}`
  return [
    Processes.start(processId),
    bind(
      Request.createRequest({
        backendType: Constants.BackendType.TRANSFERWORLD,
        bodyTemplate: {
          beneficiaryName: false,
          beneficiaryStreet: false,
          beneficiaryZip: false,
          beneficiaryCity: false,
          beneficiaryState: false,
          beneficiaryCountry: false,
          iban: false,
          swift: false,
          bankName: false,
          bankAddress: false,
          bankCity: false,
          bankZip: false,
          bankCountry: false,
          amount_cents: true,
          transaction_type: true,
        },
        requestData: R.merge(transactionParams, {
          amount_cents: params.amountCents,
          transaction_type: params.transactionType,
        }),
        requestMethod: 'POST',
        requestHeaders: {
          Accept: 'application/vnd.casinosaga.v1, application/json',
          Authorization: R.defaultTo('', params.sessionId),
        },
        urlTemplate: '/transfer_world/payment',
      }),
      (res) => {
        const redirectUrl = res.value.redirect_url

        return [
          R.isNil(redirectUrl)
            ? Actions.startTransaction({
                transactionState: Constants.TransactionState.SUCCESSFUL,
              })
            : Actions.startTransaction({
                redirectOutput: {url: redirectUrl, container: 'window'},
                transactionState: Constants.TransactionState.WAITING_INPUT,
              }),
          Processes.stop(processId),
        ]
      },
      (error) => {
        if (process.env.NODE_ENV !== 'production') {
          // Pass real error to console for easier debugging
          // eslint-disable-next-line no-console
          console.error(error)
        }

        return [
          Actions.completeTransaction(
            new Error('errors.transfer-world.perform-transaction-failure')
          ),
          Processes.stop(processId),
        ]
      }
    ),
  ]
}
